import type { MomentoGrupo, MomentoPesquisa, PesquisaTipo } from '../types/pesquisaSatisfacaoTypes';

export const TETO_DIAS_ENTRE_PESQUISAS = 7;

export const DIAS_CARENCIA_ENTRADA = 7;

/**
 * Duas dispensas seguidas são um recado: a pesquisa some por 90 dias para aquela
 * pessoa. É o principal freio contra fadiga agora que o teto é semanal — sem ele,
 * quem claramente não quer participar seria perguntado quatro vezes por mês.
 */
export const RECUSAS_CONSECUTIVAS_PARA_PAUSA = 2;

export const DIAS_PAUSA_APOS_RECUSAS = 90;

/**
 * Teto do comentário. Mesmo número do backend: cortar aqui é o que impede a pessoa
 * de digitar algo que a API recusaria depois de a nota já estar registrada.
 */
export const COMENTARIO_MAX_CARACTERES = 2000;

export const SEGUNDOS_ATE_RECOLHER = 20;

export const SEGUNDOS_ATE_FECHAR_APOS_ENVIO = 3;

/**
 * A Central de Atendimentos ocupa a tela inteira e é onde a pessoa conversa com o
 * outro lado. Perguntar ali é interromper um atendimento em curso, então a pesquisa
 * cede a vez — é o que a RN-09 chama de "com o painel de chat aberto, a pesquisa não
 * aparece". Quem aplica o recorte é o shell, NÃO montando o cartão: montar e devolver
 * `null` deixaria os efeitos do hook consumirem a pesquisa em silêncio.
 */
export const ROTA_CENTRAL_ATENDIMENTOS = '/chat';

export const PRIORIDADE_GRUPO: Record<MomentoGrupo, number> = {
  PRIMEIRO_SINAL: 1,
  ESCALONAMENTO: 2,
  DESFECHO: 3,
  CRIACAO: 4,
  ENTRADA: 5,
  ROTINA: 6,
};

export const ROTULO_GRUPO: Record<MomentoGrupo, string> = {
  PRIMEIRO_SINAL: 'Primeiro sinal do outro lado',
  ESCALONAMENTO: 'Escalonamento entre produtos',
  DESFECHO: 'Primeiro desfecho de valor',
  CRIACAO: 'Primeira criação',
  ENTRADA: 'Entrada na plataforma',
  ROTINA: 'Rotina e conta',
};

export const EXTREMOS_ESCALA: Record<PesquisaTipo, { minimo: string; maximo: string }> = {
  CSAT: { minimo: 'Foi sofrido', maximo: 'Foi tranquilo' },
  CES: { minimo: 'Discordo totalmente', maximo: 'Concordo totalmente' },
};

export const NOTA_MAXIMA: Record<PesquisaTipo, number> = {
  CSAT: 5,
  CES: 7,
};

const SUBTITULO_PRIMEIRA_VEZ =
  'Sua primeira vez — dez segundos e a gente não pergunta de novo esta semana.';

const SUBTITULO_PADRAO = 'Dez segundos e a gente não pergunta de novo esta semana.';

const SUBTITULO_CONCORDA = 'Quanto você concorda? Uma pergunta, dez segundos.';

const PLACEHOLDER_LIVRE = 'Escreva com suas palavras — a gente lê tudo.';

const PLACEHOLDER_CURTO = 'Uma linha basta.';

export const CATALOGO_MOMENTOS: MomentoPesquisa[] = [
  {
    id: 'E1',
    tipo: 'CES',
    grupo: 'ENTRADA',
    disparo: 'CARENCIA',
    evento: 'conta.onboarding.concluido',
    eventoConfirmado: true,
    rotaPadrao: null,
    produto: 'plataforma',
    fluxo: 'onboarding',
    tela: 'Entrada na plataforma',
    titulo: 'Configurar sua conta na Arbitralis foi fácil.',
    subtitulo: SUBTITULO_CONCORDA,
    perguntaBaixa: 'O que ficou confuso na configuração?',
    perguntaAlta: 'O que ajudou mais na configuração?',
    placeholderBaixa: 'Qual etapa deu mais volta — equipe, produto, dados do escritório?',
    placeholderAlta: PLACEHOLDER_CURTO,
    amarra: 'Ativação · tempo até primeiro valor',
    grupoColisao: null,
    isentoDoTeto: false,
    recorrenciaDias: null,
    versaoCopy: 1,
  },
  {
    id: 'C1',
    tipo: 'CES',
    grupo: 'CRIACAO',
    disparo: 'EVENTO',
    evento: 'notificacao.individual.enviada',
    eventoConfirmado: true,
    rotaPadrao: '/notificacoes/criar/individual',
    produto: 'notificacao',
    fluxo: 'individual',
    tela: 'Nova notificação individual',
    titulo: 'A Arbitralis facilitou enviar essa notificação.',
    subtitulo: SUBTITULO_PRIMEIRA_VEZ,
    perguntaBaixa: 'Onde você perdeu mais tempo?',
    perguntaAlta: 'O que resolveu pra você?',
    placeholderBaixa: 'Qual etapa deu mais volta — dados, documento, método de envio?',
    placeholderAlta: PLACEHOLDER_CURTO,
    amarra: 'KR 16.000 notificações',
    grupoColisao: 'notificacao-primeira-criacao',
    isentoDoTeto: false,
    recorrenciaDias: null,
    versaoCopy: 1,
  },
  {
    id: 'C2',
    tipo: 'CES',
    grupo: 'CRIACAO',
    disparo: 'EVENTO',
    evento: 'notificacao.lote.processado',
    eventoConfirmado: true,
    rotaPadrao: '/notificacoes/lotes/[id]',
    produto: 'notificacao',
    fluxo: 'lote',
    tela: 'Detalhe do lote de notificações',
    titulo: 'A Arbitralis facilitou montar esse lote.',
    subtitulo: SUBTITULO_PRIMEIRA_VEZ,
    perguntaBaixa: 'Onde você perdeu mais tempo?',
    perguntaAlta: 'O que resolveu pra você?',
    placeholderBaixa: 'Qual etapa deu mais volta — planilha, erros, método de envio?',
    placeholderAlta: PLACEHOLDER_CURTO,
    amarra: 'KR 16.000 notificações · Error Rate',
    grupoColisao: 'notificacao-primeira-criacao',
    isentoDoTeto: false,
    recorrenciaDias: null,
    versaoCopy: 1,
  },
  {
    id: 'C3',
    tipo: 'CES',
    grupo: 'CRIACAO',
    disparo: 'EVENTO',
    evento: 'negociacao.individual.ativa',
    // a tela de sucesso da negociação individual roda com dado mocado e nenhuma rota navega até ela.
    eventoConfirmado: false,
    rotaPadrao: '/negociacoes/nova/individual',
    produto: 'negociacao',
    fluxo: 'individual',
    tela: 'Nova negociação individual',
    titulo: 'A Arbitralis facilitou colocar essa negociação no ar.',
    subtitulo: SUBTITULO_PRIMEIRA_VEZ,
    perguntaBaixa: 'Onde você perdeu mais tempo?',
    perguntaAlta: 'O que resolveu pra você?',
    placeholderBaixa: 'Qual etapa deu mais volta — dados do caso, parâmetros, publicação?',
    placeholderAlta: PLACEHOLDER_CURTO,
    amarra: 'KR 22.000 casos',
    grupoColisao: 'negociacao-primeira-criacao',
    isentoDoTeto: false,
    recorrenciaDias: null,
    versaoCopy: 1,
  },
  {
    id: 'C4',
    tipo: 'CES',
    grupo: 'CRIACAO',
    disparo: 'EVENTO',
    evento: 'negociacao.lote.processado',
    eventoConfirmado: true,
    rotaPadrao: '/negociacoes/lotes/[id]',
    produto: 'negociacao',
    fluxo: 'lote',
    tela: 'Detalhe do lote de negociações',
    titulo: 'A Arbitralis facilitou processar esse lote de negociações.',
    subtitulo: SUBTITULO_PRIMEIRA_VEZ,
    perguntaBaixa: 'Onde você perdeu mais tempo?',
    perguntaAlta: 'O que resolveu pra você?',
    placeholderBaixa: 'Qual etapa deu mais volta — planilha, erros, parâmetros?',
    placeholderAlta: PLACEHOLDER_CURTO,
    amarra: 'KR 22.000 casos · Error Rate',
    grupoColisao: 'negociacao-primeira-criacao',
    isentoDoTeto: false,
    recorrenciaDias: null,
    versaoCopy: 1,
  },
  {
    id: 'C5',
    tipo: 'CES',
    grupo: 'CRIACAO',
    disparo: 'EVENTO',
    evento: 'arbitral.processo.instaurado',
    eventoConfirmado: true,
    rotaPadrao: '/arbitral/novo',
    produto: 'arbitragem',
    fluxo: 'individual',
    tela: 'Novo processo arbitral',
    titulo: 'A Arbitralis facilitou instaurar esse processo.',
    subtitulo: SUBTITULO_PRIMEIRA_VEZ,
    perguntaBaixa: 'Onde você perdeu mais tempo?',
    perguntaAlta: 'O que resolveu pra você?',
    placeholderBaixa: 'Qual etapa deu mais volta — partes, documentos, conflito de interesse?',
    placeholderAlta: PLACEHOLDER_CURTO,
    amarra: 'KR 12.000 processos',
    grupoColisao: 'arbitral-primeira-criacao',
    isentoDoTeto: false,
    recorrenciaDias: null,
    versaoCopy: 1,
  },
  {
    id: 'C6',
    tipo: 'CES',
    grupo: 'CRIACAO',
    disparo: 'EVENTO',
    evento: 'arbitral.lote.processado',
    // o lote arbitral ainda é mock (`persistMockLoteArbitral`) — não existe lote de verdade para medir.
    eventoConfirmado: false,
    rotaPadrao: '/arbitral/lote',
    produto: 'arbitragem',
    fluxo: 'lote',
    tela: 'Lote de processos arbitrais',
    titulo: 'A Arbitralis facilitou instaurar esse lote de processos.',
    subtitulo: SUBTITULO_PRIMEIRA_VEZ,
    perguntaBaixa: 'Onde você perdeu mais tempo?',
    perguntaAlta: 'O que resolveu pra você?',
    placeholderBaixa: 'Qual etapa deu mais volta — planilha, partes, documentos?',
    placeholderAlta: PLACEHOLDER_CURTO,
    amarra: 'KR 12.000 processos',
    grupoColisao: 'arbitral-primeira-criacao',
    isentoDoTeto: false,
    recorrenciaDias: null,
    versaoCopy: 1,
  },
  {
    id: 'C7',
    tipo: 'CES',
    grupo: 'CRIACAO',
    disparo: 'EVENTO',
    evento: 'regua.individual.ativada',
    eventoConfirmado: true,
    rotaPadrao: '/regua-de-cobranca/nova/individual',
    produto: 'regua',
    fluxo: 'individual',
    tela: 'Nova régua de cobrança',
    titulo: 'A Arbitralis facilitou ativar essa régua de cobrança.',
    subtitulo: SUBTITULO_PRIMEIRA_VEZ,
    perguntaBaixa: 'Onde você perdeu mais tempo?',
    perguntaAlta: 'O que resolveu pra você?',
    placeholderBaixa: 'Qual etapa deu mais volta — etapas da régua, textos, canais?',
    placeholderAlta: PLACEHOLDER_CURTO,
    amarra: 'Engajamento em funcionalidades-chave',
    grupoColisao: 'regua-primeira-ativacao',
    isentoDoTeto: false,
    recorrenciaDias: null,
    versaoCopy: 1,
  },
  {
    id: 'C8',
    tipo: 'CES',
    grupo: 'CRIACAO',
    disparo: 'EVENTO',
    evento: 'regua.lote.ativada',
    eventoConfirmado: true,
    rotaPadrao: '/regua-de-cobranca/nova/lote',
    produto: 'regua',
    fluxo: 'lote',
    tela: 'Régua de cobrança em lote',
    titulo: 'A Arbitralis facilitou ativar essa régua em lote.',
    subtitulo: SUBTITULO_PRIMEIRA_VEZ,
    perguntaBaixa: 'Onde você perdeu mais tempo?',
    perguntaAlta: 'O que resolveu pra você?',
    placeholderBaixa: 'Qual etapa deu mais volta — planilha, etapas da régua, canais?',
    placeholderAlta: PLACEHOLDER_CURTO,
    amarra: 'Engajamento em funcionalidades-chave',
    grupoColisao: 'regua-primeira-ativacao',
    isentoDoTeto: false,
    recorrenciaDias: null,
    versaoCopy: 1,
  },
  {
    id: 'S1',
    tipo: 'CES',
    grupo: 'CRIACAO',
    disparo: 'EVENTO',
    evento: 'self.notificacao.contratada',
    // O funil self roda anônimo, antes do login: quem observa é o SERVIDOR, que
    // devolve o momento em `eventosPendentes` a partir do lead convertido.
    eventoConfirmado: true,
    rotaPadrao: '/notificacao-self',
    produto: 'notificacao',
    fluxo: 'self',
    tela: 'Contratação self de notificação',
    titulo: 'Contratar a Arbitralis por conta própria foi fácil.',
    subtitulo: SUBTITULO_CONCORDA,
    perguntaBaixa: 'Onde você travou na contratação?',
    perguntaAlta: 'O que resolveu pra você?',
    placeholderBaixa: 'Qual etapa deu mais volta — CNPJ, plano, pagamento?',
    placeholderAlta: PLACEHOLDER_CURTO,
    amarra: 'Conversão self-service · ativação',
    grupoColisao: 'notificacao-primeira-criacao',
    isentoDoTeto: false,
    recorrenciaDias: null,
    versaoCopy: 1,
  },
  {
    id: 'S2',
    tipo: 'CES',
    grupo: 'CRIACAO',
    disparo: 'EVENTO',
    evento: 'self.negociacao.contratada',
    // O funil self roda anônimo, antes do login: quem observa é o SERVIDOR, que
    // devolve o momento em `eventosPendentes` a partir do lead convertido.
    eventoConfirmado: true,
    rotaPadrao: '/negociacao-self',
    produto: 'negociacao',
    fluxo: 'self',
    tela: 'Contratação self de negociação',
    titulo: 'Criar seu agente de negociação por conta própria foi fácil.',
    subtitulo: SUBTITULO_CONCORDA,
    perguntaBaixa: 'Onde você travou na contratação?',
    perguntaAlta: 'O que resolveu pra você?',
    placeholderBaixa: 'Qual etapa deu mais volta — objetivo, parâmetros, plano, pagamento?',
    placeholderAlta: PLACEHOLDER_CURTO,
    amarra: 'Conversão self-service · ativação',
    grupoColisao: 'negociacao-primeira-criacao',
    isentoDoTeto: false,
    recorrenciaDias: null,
    versaoCopy: 1,
  },
  {
    id: 'R1',
    tipo: 'CSAT',
    grupo: 'PRIMEIRO_SINAL',
    disparo: 'EVENTO',
    evento: 'notificacao.manifestacao.recebida',
    // a manifestação chega pelo servidor; o front só a veria ao abrir a tela, que é consulta.
    eventoConfirmado: false,
    rotaPadrao: '/notificacoes/[id]',
    produto: 'notificacao',
    fluxo: 'acompanhamento',
    tela: 'Detalhe da notificação',
    titulo: 'Como foi receber a primeira manifestação?',
    subtitulo: SUBTITULO_PADRAO,
    perguntaBaixa: 'O que decepcionou nessa resposta?',
    perguntaAlta: 'O que funcionou bem?',
    placeholderBaixa: PLACEHOLDER_LIVRE,
    placeholderAlta: PLACEHOLDER_CURTO,
    amarra: 'KR manifestação 30% · hoje 3,86%',
    grupoColisao: null,
    isentoDoTeto: false,
    recorrenciaDias: null,
    versaoCopy: 1,
  },
  {
    id: 'R2',
    tipo: 'CSAT',
    grupo: 'PRIMEIRO_SINAL',
    disparo: 'EVENTO',
    evento: 'negociacao.mensagem.inbound.primeira',
    // a primeira resposta do devedor chega pelo servidor, não por ação de quem opera.
    eventoConfirmado: false,
    rotaPadrao: '/negociacoes/[id]',
    produto: 'negociacao',
    fluxo: 'acompanhamento',
    tela: 'Detalhe da negociação',
    titulo: 'O devedor respondeu. O que você achou da conversa?',
    subtitulo: SUBTITULO_PADRAO,
    perguntaBaixa: 'O que ficou ruim na conversa?',
    perguntaAlta: 'O que funcionou bem na conversa?',
    placeholderBaixa: PLACEHOLDER_LIVRE,
    placeholderAlta: PLACEHOLDER_CURTO,
    amarra: 'KR leitura de mensagens 60% · hoje 45,8%',
    grupoColisao: null,
    isentoDoTeto: false,
    recorrenciaDias: null,
    versaoCopy: 1,
  },
  {
    id: 'R3',
    tipo: 'CSAT',
    grupo: 'PRIMEIRO_SINAL',
    disparo: 'EVENTO',
    evento: 'arbitral.requerido.manifestacao.primeira',
    // a manifestação do requerido chega pelo servidor.
    eventoConfirmado: false,
    rotaPadrao: '/arbitral/[id]',
    produto: 'arbitragem',
    fluxo: 'acompanhamento',
    tela: 'Detalhe do processo arbitral',
    titulo: 'O requerido se manifestou. Como foi acompanhar isso?',
    subtitulo: SUBTITULO_PADRAO,
    perguntaBaixa: 'O que ficou difícil de acompanhar?',
    perguntaAlta: 'O que funcionou bem?',
    placeholderBaixa: PLACEHOLDER_LIVRE,
    placeholderAlta: PLACEHOLDER_CURTO,
    amarra: 'KR leitura e manifestação da citação · hoje 58,2%',
    grupoColisao: null,
    isentoDoTeto: false,
    recorrenciaDias: null,
    versaoCopy: 1,
  },
  {
    id: 'D1',
    tipo: 'CSAT',
    grupo: 'DESFECHO',
    disparo: 'EVENTO',
    evento: 'negociacao.acordo.fechado',
    // quem fecha o acordo é a Lexi, na conversa — não há ação do operador para amarrar.
    eventoConfirmado: false,
    rotaPadrao: '/negociacoes/[id]',
    produto: 'negociacao',
    fluxo: 'acordo',
    tela: 'Detalhe da negociação',
    titulo: 'Primeiro acordo fechado. Como foi chegar até aqui?',
    subtitulo: SUBTITULO_PADRAO,
    perguntaBaixa: 'O que atrapalhou no caminho?',
    perguntaAlta: 'O que mais ajudou a fechar?',
    placeholderBaixa: PLACEHOLDER_LIVRE,
    placeholderAlta: PLACEHOLDER_CURTO,
    amarra: 'Taxa de acordos · hoje 5,49%',
    grupoColisao: null,
    isentoDoTeto: false,
    recorrenciaDias: null,
    versaoCopy: 1,
  },
  {
    id: 'D2',
    tipo: 'CSAT',
    grupo: 'DESFECHO',
    disparo: 'EVENTO',
    evento: 'notificacao.certificado.emitido',
    // a emissão do certificado é do servidor.
    eventoConfirmado: false,
    rotaPadrao: '/notificacoes/certificado/[id]',
    produto: 'notificacao',
    fluxo: 'certificado',
    tela: 'Certificado da notificação',
    titulo: 'Seu primeiro certificado está pronto. Ele te dá segurança?',
    subtitulo: SUBTITULO_PADRAO,
    perguntaBaixa: 'O que falta nesse certificado?',
    perguntaAlta: 'O que ele resolve pra você?',
    placeholderBaixa: PLACEHOLDER_LIVRE,
    placeholderAlta: PLACEHOLDER_CURTO,
    amarra: 'Prova jurídica · confiança',
    grupoColisao: null,
    isentoDoTeto: false,
    recorrenciaDias: null,
    versaoCopy: 1,
  },
  {
    id: 'D3',
    tipo: 'CSAT',
    grupo: 'DESFECHO',
    disparo: 'EVENTO',
    evento: 'arbitral.sentenca.publicada',
    // a publicação da sentença é da Secretaria, fora do app do escritório.
    eventoConfirmado: false,
    rotaPadrao: '/arbitral/[id]/sentenca',
    produto: 'arbitragem',
    fluxo: 'sentenca',
    tela: 'Sentença do processo arbitral',
    titulo: 'Primeira sentença publicada. Como foi o processo até aqui?',
    subtitulo: SUBTITULO_PADRAO,
    perguntaBaixa: 'O que mais pesou no processo?',
    perguntaAlta: 'O que mais ajudou no processo?',
    placeholderBaixa: PLACEHOLDER_LIVRE,
    placeholderAlta: PLACEHOLDER_CURTO,
    amarra: 'Tempo início → sentença',
    grupoColisao: null,
    isentoDoTeto: false,
    recorrenciaDias: null,
    versaoCopy: 1,
  },
  {
    id: 'D4',
    tipo: 'CSAT',
    grupo: 'DESFECHO',
    disparo: 'EVENTO',
    evento: 'pagamento.recebimento.confirmado',
    // a confirmação do pagamento chega por webhook.
    eventoConfirmado: false,
    rotaPadrao: null,
    produto: 'negociacao',
    fluxo: 'pagamento',
    tela: null,
    titulo: 'O pagamento entrou. Como foi essa etapa?',
    subtitulo: SUBTITULO_PADRAO,
    perguntaBaixa: 'O que complicou o recebimento?',
    perguntaAlta: 'O que funcionou bem no recebimento?',
    placeholderBaixa: PLACEHOLDER_LIVRE,
    placeholderAlta: PLACEHOLDER_CURTO,
    amarra: 'Recuperação · NRR',
    grupoColisao: null,
    isentoDoTeto: false,
    recorrenciaDias: null,
    versaoCopy: 1,
  },
  {
    id: 'X1',
    tipo: 'CES',
    grupo: 'ESCALONAMENTO',
    disparo: 'EVENTO',
    evento: 'escalonamento.notificacao.negociacao',
    // o escalonamento notificação→negociação não foi localizado no front.
    eventoConfirmado: false,
    rotaPadrao: null,
    produto: 'notificacao',
    fluxo: 'escalonamento',
    tela: null,
    titulo: 'Levar essa notificação para negociação foi fácil.',
    subtitulo: SUBTITULO_CONCORDA,
    perguntaBaixa: 'O que travou na virada para negociação?',
    perguntaAlta: 'O que resolveu pra você?',
    placeholderBaixa: 'Qual etapa deu mais volta — dados do caso, parâmetros, canais?',
    placeholderAlta: PLACEHOLDER_CURTO,
    amarra: 'Ecossistema 50% · hoje 11,1%',
    grupoColisao: null,
    isentoDoTeto: false,
    recorrenciaDias: null,
    versaoCopy: 1,
  },
  {
    id: 'X2',
    tipo: 'CES',
    grupo: 'ESCALONAMENTO',
    disparo: 'EVENTO',
    evento: 'escalonamento.negociacao.arbitragem',
    // o escalonamento negociação→arbitragem não foi localizado no front.
    eventoConfirmado: false,
    rotaPadrao: null,
    produto: 'negociacao',
    fluxo: 'escalonamento',
    tela: null,
    titulo: 'Levar essa negociação para arbitragem foi fácil.',
    subtitulo: SUBTITULO_CONCORDA,
    perguntaBaixa: 'O que travou na virada para arbitragem?',
    perguntaAlta: 'O que resolveu pra você?',
    placeholderBaixa: 'Qual etapa deu mais volta — partes, documentos, cláusula?',
    placeholderAlta: PLACEHOLDER_CURTO,
    amarra: 'Ecossistema · North Star de contratos',
    grupoColisao: null,
    isentoDoTeto: false,
    recorrenciaDias: null,
    versaoCopy: 1,
  },
  {
    id: 'V1',
    tipo: 'CES',
    grupo: 'ROTINA',
    disparo: 'EVENTO',
    evento: 'lote.quinto.processado',
    // a contagem do quinto lote é agregada e o front não a conhece.
    eventoConfirmado: false,
    rotaPadrao: null,
    produto: 'plataforma',
    fluxo: 'lote',
    tela: null,
    titulo: 'A Arbitralis dá conta do seu volume de lotes.',
    subtitulo: SUBTITULO_CONCORDA,
    perguntaBaixa: 'O que trava quando o volume cresce?',
    perguntaAlta: 'O que segura bem o volume?',
    placeholderBaixa: 'Qual etapa deu mais volta — importação, erros, acompanhamento?',
    placeholderAlta: PLACEHOLDER_CURTO,
    amarra: 'O fluxo aguenta volume?',
    grupoColisao: null,
    isentoDoTeto: false,
    recorrenciaDias: 90,
    versaoCopy: 1,
  },
  {
    id: 'P1',
    tipo: 'CSAT',
    grupo: 'ROTINA',
    disparo: 'EVENTO',
    evento: 'plano.renovado',
    // a renovação do plano é do servidor.
    eventoConfirmado: false,
    rotaPadrao: null,
    produto: 'plataforma',
    fluxo: 'plano',
    tela: null,
    titulo: 'Você renovou seu plano. Valeu a pena?',
    subtitulo: SUBTITULO_PADRAO,
    perguntaBaixa: 'O que faltou pra valer a pena?',
    perguntaAlta: 'O que mais valeu a pena?',
    placeholderBaixa: PLACEHOLDER_LIVRE,
    placeholderAlta: PLACEHOLDER_CURTO,
    amarra: 'Churn · NRR',
    grupoColisao: null,
    isentoDoTeto: false,
    recorrenciaDias: null,
    versaoCopy: 1,
  },
];

export const MOMENTOS_POR_ID: Record<string, MomentoPesquisa> = Object.fromEntries(
  CATALOGO_MOMENTOS.map((momento) => [momento.id, momento]),
);

export const MOMENTOS_POR_EVENTO: Record<string, MomentoPesquisa> = Object.fromEntries(
  CATALOGO_MOMENTOS.map((momento) => [momento.evento, momento]),
);
